import type {
  AtoReport,
  AuditEvent,
  CapitalCall,
  Distribution,
  Holder,
  InvestorTask,
  LedgerEntry,
  MigrationJob,
  RegistryDocument,
  TaxStatementBatch,
} from '../types';
import { FUNDS } from './seedReference';

export function buildTaxBatches(): TaxStatementBatch[] {
  return FUNDS.filter((fund) => fund.id !== 'f-sdf2').flatMap((fund) => [
    {
      id: `tb-${fund.id}-26`,
      fundId: fund.id,
      financialYear: 'FY2026',
      status: 'PUBLISHED' as const,
      statementCount: 0,
      componentsSource: `Archive imported from ${fund.migratedFrom ?? 'previous registry'}`,
      publishedAt: '2026-09-15',
    },
    {
      id: `tb-${fund.id}-27`,
      fundId: fund.id,
      financialYear: 'FY2027',
      status: 'NOT_STARTED' as const,
      statementCount: 0,
    },
  ]);
}

export const ATO_REPORTS: AtoReport[] = [
  {
    id: 'ato-aiir-26',
    kind: 'AIIR',
    period: 'FY2026',
    dueDate: '2026-10-31',
    status: 'DRAFT',
    records: 0,
    issues: 2,
  },
  {
    id: 'ato-aiir-27',
    kind: 'AIIR',
    period: 'FY2027',
    dueDate: '2027-10-31',
    status: 'NOT_STARTED',
    records: 0,
    issues: 0,
  },
  {
    id: 'ato-tfn-q1',
    kind: 'TFN_REPORT',
    period: 'Jul–Sep 2026',
    dueDate: '2026-10-28',
    status: 'DRAFT',
    records: 0,
    issues: 0,
  },
  {
    id: 'ato-fatca-25',
    kind: 'FATCA',
    period: 'CY2025',
    dueDate: '2026-07-31',
    status: 'LODGED',
    records: 2,
    issues: 0,
    lodgedAt: '2026-07-24',
    receipt: 'ATO-FTC-88213470',
  },
  {
    id: 'ato-crs-25',
    kind: 'CRS',
    period: 'CY2025',
    dueDate: '2026-07-31',
    status: 'LODGED',
    records: 5,
    issues: 0,
    lodgedAt: '2026-07-24',
    receipt: 'ATO-CRS-88213471',
  },
  {
    id: 'ato-aiir-25',
    kind: 'AIIR',
    period: 'FY2025',
    dueDate: '2025-10-31',
    status: 'LODGED',
    records: 118,
    issues: 0,
    lodgedAt: '2025-10-22',
    receipt: 'Lodged by previous registry',
  },
  {
    id: 'ato-fatca-26',
    kind: 'FATCA',
    period: 'CY2026',
    dueDate: '2027-07-31',
    status: 'NOT_STARTED',
    records: 0,
    issues: 0,
  },
  {
    id: 'ato-crs-26',
    kind: 'CRS',
    period: 'CY2026',
    dueDate: '2027-07-31',
    status: 'NOT_STARTED',
    records: 0,
    issues: 0,
  },
];

export function buildTasks(holders: Holder[], call: CapitalCall): InvestorTask[] {
  const tasks: InvestorTask[] = [];
  for (const line of call.lines) {
    if (line.status === 'PAID') continue;
    tasks.push({
      id: `t-call-${line.holderId}`,
      holderId: line.holderId,
      kind: 'PAY_CALL',
      title: 'Pay OPP1 capital call 3',
      detail: `Outstanding ${(line.amount - line.paid).toLocaleString('en-AU', { style: 'currency', currency: 'AUD' })}. BPAY biller 384522, reference ${line.crn}.`,
      dueDate: call.dueDate,
      status: 'OPEN',
      refId: call.id,
    });
  }
  for (const holder of holders) {
    if (holder.tenantId !== 't-fides') continue;
    if (holder.tfnStatus === 'NOT_QUOTED') {
      tasks.push({
        id: `t-tfn-${holder.id}`,
        holderId: holder.id,
        kind: 'QUOTE_TFN',
        title: 'Provide a TFN or ABN',
        detail: 'Without a TFN or ABN we must withhold 47% of each distribution.',
        status: 'OPEN',
      });
    }
    if (holder.wholesale?.expiresAt && holder.wholesale.expiresAt <= '2026-12-31') {
      tasks.push({
        id: `t-ws-${holder.id}`,
        holderId: holder.id,
        kind: 'RENEW_WHOLESALE',
        title: 'Renew your wholesale investor certificate',
        detail: `Your accountant's certificate expires ${holder.wholesale.expiresAt}. Upload a new one to keep investing in new offers.`,
        dueDate: holder.wholesale.expiresAt,
        status: 'OPEN',
      });
    }
    if (holder.kycStatus === 'EXPIRED') {
      tasks.push({
        id: `t-kyc-${holder.id}`,
        holderId: holder.id,
        kind: 'REFRESH_KYC',
        title: 'Re-confirm your identity',
        detail: 'Our three-yearly identity check is due. It takes about 3 minutes.',
        dueDate: holder.kycRefreshDue,
        status: 'OPEN',
      });
    }
  }
  tasks.push({
    id: 't-app-bennett',
    holderId: 'h-bennett-trust',
    kind: 'COMPLETE_APPLICATION',
    title: 'Finish your SDF2 application',
    detail: '4 of 8 steps complete. Identity, wholesale certificate, signing and payment remain.',
    dueDate: '2026-10-31',
    status: 'OPEN',
    refId: 'app-1',
  });
  tasks.push({
    id: 't-sign-mehta',
    holderId: 'h-mehta-co',
    kind: 'SIGN_DOCUMENT',
    title: 'Start your SDF2 application',
    detail:
      'You expressed interest in Fides Senior Debt Fund II. Your existing details are pre-filled.',
    dueDate: '2026-10-31',
    status: 'OPEN',
    refId: 'app-7',
  });
  return tasks;
}

export function buildDocuments(
  holders: Holder[],
  distributions: Distribution[],
  call: CapitalCall,
): RegistryDocument[] {
  const documents: RegistryDocument[] = [];
  const fundCode = (fundId: string) => FUNDS.find((fund) => fund.id === fundId)?.code ?? fundId;
  for (const distribution of distributions.filter((item) => item.status === 'PAID')) {
    for (const line of distribution.lines) {
      documents.push({
        id: `doc-${distribution.id}-${line.holderId}`,
        holderId: line.holderId,
        fundId: distribution.fundId,
        kind: 'DISTRIBUTION_ADVICE',
        refId: distribution.id,
        name: `${fundCode(distribution.fundId)} distribution advice, period ending ${distribution.periodEnd}`,
        date: distribution.paymentDate,
        sizeKb: 84,
        visibleToInvestor: true,
      });
    }
  }
  for (const line of call.lines) {
    documents.push({
      id: `doc-call3-${line.holderId}`,
      holderId: line.holderId,
      fundId: call.fundId,
      kind: 'CALL_NOTICE',
      refId: call.id,
      name: 'OPP1 capital call notice 3',
      date: call.issueDate,
      sizeKb: 112,
      visibleToInvestor: true,
    });
  }
  for (const holder of holders.filter((item) => item.tenantId === 't-fides')) {
    documents.push({
      id: `doc-tax26-${holder.id}`,
      holderId: holder.id,
      kind: 'TAX_STATEMENT',
      refId: 'FY2026',
      name: 'FY2026 AMMA tax statement (imported from previous registry)',
      date: '2026-09-15',
      sizeKb: 140,
      visibleToInvestor: true,
    });
    documents.push({
      id: `doc-hs-${holder.id}`,
      holderId: holder.id,
      kind: 'HOLDING_STATEMENT',
      name: 'Holding statement as at 30 June 2026',
      date: '2026-07-15',
      sizeKb: 66,
      visibleToInvestor: true,
    });
    if (holder.wholesale) {
      documents.push({
        id: `doc-ws-${holder.id}`,
        holderId: holder.id,
        kind: 'WHOLESALE_CERTIFICATE',
        name: `Wholesale certificate (${holder.wholesale.issuer})`,
        date: holder.wholesale.issuedAt,
        sizeKb: 210,
        visibleToInvestor: true,
      });
    }
    documents.push({
      id: `doc-id-${holder.id}`,
      holderId: holder.id,
      kind: 'ID_DOCUMENT',
      name: 'Identity verification report (GreenID)',
      date: holder.kycVerifiedAt ?? holder.createdAt,
      sizeKb: 48,
      visibleToInvestor: false,
    });
  }
  return documents;
}

export function buildAudit(ledger: LedgerEntry[]): AuditEvent[] {
  const events: AuditEvent[] = [
    {
      id: 'au-1',
      at: '2026-06-30T17:00:00',
      actor: 'Alex Morgan',
      action: 'Migration submitted',
      target: '4 funds from Registry Direct',
      detail: 'Opening balances reconciled to the provider register as at 30 Jun 2026.',
    },
    {
      id: 'au-2',
      at: '2026-07-01T08:30:00',
      actor: 'Priya Nair',
      action: 'Migration approved',
      target: 'PSDF, OPP1, MEZ2, CORE',
      detail: 'Native register live from 1 Jul 2026.',
    },
    {
      id: 'au-3',
      at: '2026-09-30T10:30:00',
      actor: 'Priya Nair',
      action: 'Capital call approved',
      target: 'OPP1 Call 3',
      detail: '20% of commitments, due 24 Oct 2026.',
    },
    {
      id: 'au-4',
      at: '2026-10-09T09:10:00',
      actor: 'Priya Nair',
      action: 'Distribution approved',
      target: 'MEZ2 Sep quarter',
      detail: 'ABA file generated and released to bank.',
    },
    {
      id: 'au-5',
      at: '2026-10-15T16:20:00',
      actor: 'Alex Morgan',
      action: 'Distribution submitted for approval',
      target: 'OPP1 Sep quarter',
      detail: '2.5 cpu on paid units.',
    },
  ];
  const recent = ledger.filter((entry) => entry.type === 'CALL_PAYMENT').slice(0, 6);
  recent.forEach((entry, index) =>
    events.push({
      id: `au-l${index}`,
      at: entry.postedAt,
      actor: 'Alex Morgan',
      action: 'Payment matched',
      target: `Call 3 · ${entry.holderId}`,
      detail: `$${entry.amount.toLocaleString('en-AU')} matched from bank feed`,
    }),
  );
  return events.sort((left, right) => right.at.localeCompare(left.at));
}

export const MIGRATIONS: MigrationJob[] = [
  {
    id: 'mg-1',
    fundId: 'f-psd',
    source: 'Registry Direct',
    status: 'COMMITTED',
    fileName: 'RD_register_PSDF_20260630.csv',
    rows: 0,
    matched: 0,
    newHolders: 0,
    exceptions: 0,
    unitsVariance: 0,
    createdAt: '2026-06-30',
  },
  {
    id: 'mg-2',
    fundId: 'f-opp',
    source: 'Registry Direct',
    status: 'COMMITTED',
    fileName: 'RD_register_OPP1_20260630.csv',
    rows: 0,
    matched: 0,
    newHolders: 0,
    exceptions: 0,
    unitsVariance: 0,
    createdAt: '2026-06-30',
  },
  {
    id: 'mg-3',
    fundId: 'f-mez',
    source: 'Registry Direct',
    status: 'COMMITTED',
    fileName: 'RD_register_MEZ2_20260630.csv',
    rows: 0,
    matched: 0,
    newHolders: 0,
    exceptions: 0,
    unitsVariance: 0,
    createdAt: '2026-06-30',
  },
  {
    id: 'mg-4',
    fundId: 'f-core',
    source: 'Registry Direct',
    status: 'COMMITTED',
    fileName: 'RD_register_CORE_20260630.csv',
    rows: 0,
    matched: 0,
    newHolders: 0,
    exceptions: 0,
    unitsVariance: 0,
    createdAt: '2026-06-30',
  },
  {
    id: 'mg-5',
    fundId: 'f-hdcf',
    source: 'Automic',
    status: 'MATCHED',
    fileName: 'automic_holder_extract_HDCF.xlsx',
    rows: 18,
    matched: 15,
    newHolders: 2,
    exceptions: 1,
    unitsVariance: 0,
    createdAt: '2026-10-12',
  },
];
